import type { UseInfiniteQueryResult } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { View, type ViewProps, type ViewStyle } from 'react-native'

import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { TEST_ID } from '@/src/general/constants/testId'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import type { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'

import type { TCommunityApiHasJoinedInfos, TMediumCommunityApi } from '../../api/communityApi.types'
import type { EVENT_ORIGIN } from '../../models/community.constants'
import { CommunityCard } from './CommunityCard'
import { CommunitySearchPlaceholder } from './CommunitySearchPlaceholder'

const TAILWIND_GAP_6 = 24
const MAX_GRID_WIDTH = 1400
const HORIZONTAL_MARGIN = 40

type TCommunityGridPlaceholderProps = ViewProps & {
  nbColumns: number
  itemStyle: ViewStyle
}

const CommunityGridPlaceholder = ({
  nbColumns,
  itemStyle,
  ...viewProps
}: TCommunityGridPlaceholderProps): ReactNode => (
  <View className="flex-6 mt-4 max-w-[1400px] flex-row flex-wrap gap-6" {...viewProps}>
    {Array.from({ length: nbColumns }, (_, index) => (
      <View style={itemStyle} key={index}>
        <CommunitySearchPlaceholder />
      </View>
    ))}
  </View>
)

/**
 * Calculates the maximum width of a grid item.
 *
 * @returns {number} The computed width of a single item in the grid.
 *
 * @example
 *  Given:
 *    gridWidth = 1200px, nbColumns = 3, TAILWIND_GAP_6 = 24px
 *  Calculation:
 *    itemWidth = Math.floor(1200 / 3) - 24
 *    itemWidth = 376px
 *
 * @diagram
 * |---------------- gridWidth ---------------|
 * | item  |  gap  |  item  |  gap  |  item   |
 *
 * itemWidth = (gridWidth / nbColumns) - TAILWIND_GAP_6
 */
const getMaxWidth = (nbColumns: number, gridWidth: number): number => Math.ceil(gridWidth / nbColumns) - TAILWIND_GAP_6

/**
 * Calculates the horizontal margin to center the grid within its container.
 *
 * The margin ensures that the grid remains centered, adjusting for both
 * the total width occupied by items and the gaps between them.
 *
 * @returns {number} The computed horizontal margin.
 *
 * @example
 * Given:
 *  gridWidth = 1200px, nbColumns = 3, getMaxWidth() = 376px
 *  totalItemsWidth = nbColumns * getMaxWidth() = 1128px
 *  margin = (gridWidth - totalItemsWidth - (nbColumns - 1) * 24) / 2
 *
 * @diagram
 *  Case: gridWidth >= MAX_GRID_WIDTH
 *
 *  | margin |  item  | gap |  item  | gap |  item  | margin |
 *  |--------|--------|-----|--------|-----|--------|--------|
 *           <---- totalItemsWidth + totalGaps ---->
 *
 *  margin = (gridWidth - totalItemsWidth - totalGaps) / 2
 *
 *  Case: gridWidth < MAX_GRID_WIDTH
 *
 *  | margin (larger) | item | gap | item | gap | item | margin (larger) |
 *  |----------------|------|-----|------|-----|------|----------------|
 *                  <-- totalItemsWidth + totalGaps -->
 *
 *  margin = (gridWidth + 40 - totalItemsWidth - totalGaps) / 2
 */
const getMarginHorizontal = (nbColumns: number, gridWidth: number) => {
  const totalItemsWidth = nbColumns * getMaxWidth(nbColumns, gridWidth)
  const totalGaps = (nbColumns - 1) * 24
  if (gridWidth >= MAX_GRID_WIDTH) {
    return (gridWidth - totalItemsWidth - totalGaps) / 2
  }
  return (gridWidth + HORIZONTAL_MARGIN / 2 - totalItemsWidth - totalGaps) / 2
}

type TCommunitiesGridProps = Pick<
  UseInfiniteQueryResult,
  'fetchStatus' | 'error' | 'isPending' | 'isFetchingNextPage'
> & {
  data?: (TMediumCommunityApi & TCommunityApiHasJoinedInfos)[]
  centered?: boolean
  origin: EVENT_ORIGIN
  source: TRACKING_SOURCE
}

export const CommunitiesGrid = ({
  data,
  error,
  isPending,
  fetchStatus,
  centered,
  isFetchingNextPage,
  origin,
  source,
}: TCommunitiesGridProps): ReactNode => {
  const { xs, xm, sm, md, lg, xl, width } = useDimensions()

  const gridWidth = width > MAX_GRID_WIDTH ? MAX_GRID_WIDTH : width - HORIZONTAL_MARGIN

  const nbColumns = (() => {
    if (xs) return 1
    if (sm || xm) return 2
    if (md || lg) return 3
    if (xl && width < 1400) return 4
    if (width >= 1400) return 5
    return 5
  })()

  const containerStyle = centered ? { paddingHorizontal: getMarginHorizontal(nbColumns, gridWidth) } : {}
  const itemStyle = { width: getMaxWidth(nbColumns, gridWidth) }

  if (error) return <ErrorMessage error={error} defaultErrorKey="community.load.error" />

  return (
    <View className="items-center">
      {data && (
        <View
          style={containerStyle}
          // Updating gap-6 requires to update TAILWIND_GAP_6
          className="flex-6 mt-4 max-w-[1400px] flex-row flex-wrap gap-6"
          testID={TEST_ID.COMMUNITIES.ALL_CATEGORIES}
        >
          {data.map((community) => (
            <View style={itemStyle} key={community.id}>
              <CommunityCard
                fullWidth
                community={community}
                origin={origin}
                source={source}
                showSubscribeButton={false}
              />
            </View>
          ))}
        </View>
      )}
      {((isPending && fetchStatus !== 'idle') || isFetchingNextPage) && (
        // Updating gap-6 requires to update TAILWIND_GAP_6
        <CommunityGridPlaceholder nbColumns={nbColumns} itemStyle={itemStyle} style={containerStyle} />
      )}
    </View>
  )
}
